/**
 * NOTHING PERSONAL LEAVES THE APP.
 *
 * Error and analytics payloads travel to third parties and are retained far longer than
 * anyone assumes. A stack trace with an activation token in a URL is a credential in
 * someone else's database; a breadcrumb with an email address is a GDPR question.
 *
 * So every value is scrubbed on the way out, at the seam, rather than at each call site —
 * the call sites are where it gets forgotten.
 */
/**
 * Deliberately NOT a bare `code`: this app's error vocabulary is a field called `code`
 * (UNAUTHENTICATED, NOT_FOUND …), and redacting it stripped the single most useful value out
 * of every report — reports read `code: [redacted]`, which is worse than useless.
 * What must never leave are one-time codes and credentials, so those are named exactly.
 */
const TOKENISH = /\b(token|secret|password|authorization|apikey|api_key|otp|one_?time_?code|verification_?code|auth_?code)\b/i;
const EMAIL = /[\w.+-]+@[\w-]+\.[\w.-]+/g;
const PHONE = /\+?\d[\d\s().-]{7,}\d/g;
const JWT = /\beyJ[\w-]+\.[\w-]+\.[\w-]+/g;
const BEARER = /(bearer\s+)[\w.-]+/gi;

export function scrubString(value: string): string {
  return value
    .replace(JWT, "[token]")
    .replace(BEARER, "$1[token]")
    .replace(EMAIL, "[email]")
    .replace(PHONE, "[phone]")
    // Anything that looks like `token=…` in a URL or query string.
    .replace(/([?&](?:token|code|secret)=)[^&\s]+/gi, "$1[redacted]");
}

export function scrub(value: unknown, depth = 0): unknown {
  if (depth > 4) return "[deep]";
  if (typeof value === "string") return scrubString(value);
  if (Array.isArray(value)) return value.map((item) => scrub(item, depth + 1));
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [key, inner] of Object.entries(value as Record<string, unknown>)) {
      out[key] = TOKENISH.test(key) ? "[redacted]" : scrub(inner, depth + 1);
    }
    return out;
  }
  return value;
}
